import { createClient } from '@supabase/supabase-js';
import { createTimeoutFetch } from './lib/supabaseFetch';
import { withNativeSessionHandoff } from './lib/auth/nativeSessionHandoff';
import { readStoredAuthUserId } from './lib/auth/storedSession';
import { NATIVE_OR_SIM } from './lib/proximity/native/simPhone';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

/**
 * Swift's publisher refreshes the session on its own in a pocket; every web refresh takes its newer token first.
 * Never hand the plugin itself through a promise (resolve it, return it from an async function): a Capacitor plugin
 * answers every property, `then` included, so the promise treats it as a thenable that never settles. That stalled
 * every token refresh on the sim phones (2026-10-31). Call its methods inside, return only their results.
 */
const nativeSession = {
  async getNativeSession() {
    if (!NATIVE_OR_SIM) return null;
    const { LynxLocation } = await import('./lib/proximity/native/LynxLocation');
    return LynxLocation.getPublishTokens();
  },
  currentUserId: readStoredAuthUserId,
  async giveNativeSession(tokens) {
    const { LynxLocation } = await import('./lib/proximity/native/LynxLocation');
    return LynxLocation.updatePublishTokens(tokens);
  },
  log(event, payload) {
    nativeSessionLogger?.(event, payload);
  },
};

/** appLog imports this file, so it registers its logger here (installAppLog) rather than being imported. */
let nativeSessionLogger = null;
export function setNativeSessionLogger(fn) {
  nativeSessionLogger = fn;
}

let supabaseInstance = null;

/** Lazy singleton — client is created on first use, not at module import. */
export function getSupabase() {
  if (!supabaseInstance) {
    supabaseInstance = createClient(supabaseUrl, supabaseKey, {
      auth: {
        flowType: 'implicit',
      },
      global: {
        fetch: withNativeSessionHandoff(createTimeoutFetch(), nativeSession),
      },
      realtime: {
        params: {
          eventsPerSecond: 10,
        },
      },
    });
  }
  return supabaseInstance;
}

/**
 * Back-compat export for existing call sites.
 * Proxy defers createClient until the first property access.
 */
export const supabase = new Proxy(
  {},
  {
    get(_target, prop) {
      const client = getSupabase();
      const value = client[prop];
      return typeof value === 'function' ? value.bind(client) : value;
    },
  },
);
